import { Ref, forwardRef, useMemo, useCallback } from 'react';
import {
  BoxProps,
  FormControl,
  InputGroup,
  FormControlProps,
} from '@chakra-ui/react';
import { FieldError, FieldErrors } from 'react-hook-form';
import {
  GroupBase,
  OptionBase,
  SelectInstance,
  Select as BaseSelect,
  Props as ReactSelectProps,
} from 'chakra-react-select';

import { FormIconError } from 'components/atoms/formIconError';
import { Error } from 'components/atoms/form/error';
import { FieldTitle } from 'components/atoms/form/fieldTitle';
import getErrorMessage from 'lib/utils/getErrorMessage';
import { VariantsAvailable as FieldTitleVariantsAvailable } from 'components/atoms/form/fieldTitle/variants';

import { VariantsAvailable, variants } from './variants';

export interface SelectOption extends OptionBase {
  text: string;
  value: string | number | (() => void);
}

export type EventValue = {
  target: { name: string; value: SelectOption['value'] };
};

export type OnChangeHandler = (value: EventValue) => void;

export interface SelectProps extends Omit<ReactSelectProps, 'onChange'> {
  containerStyles?: FormControlProps;
  error?: FieldError;
  errors?: FieldErrors;
  isRequired?: boolean;
  label?: string;
  labelVariant?: FieldTitleVariantsAvailable;
  starColor?: BoxProps['color'];
  name: string;
  occultErrorMessage?: boolean;
  options: SelectOption[];
  placeholder: string;
  showErrorIcon?: boolean;
  variant?: VariantsAvailable;
  isLengthLimited?: boolean;
  isSearchable?: boolean;
  onChange: OnChangeHandler;
}

const MAX_ALLOWED_TEXT_LENGTH = 25;

const Select = forwardRef<SelectInstance, SelectProps>(
  (
    {
      containerStyles = {
        width: '100%',
      },
      variant = 'outline',
      placeholder,
      isRequired,
      labelVariant,
      starColor,
      options,
      errors, // TODO: Refactor: Remove errors prop, it is not necessary to send all errors in the form to the field, just send the specific message
      error,
      occultErrorMessage = false,
      label,
      name,
      showErrorIcon = false,
      value,
      isSearchable = true,
      isLengthLimited = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const variantStyles = variants[variant];

    const errorMessage = useMemo(
      () => (errors ? getErrorMessage(name, errors) : error?.message),
      [errors, error, name]
    );

    const selectedOption = useMemo(() => {
      const targetOption = options.find((c) => c.value === value) || null;
      if (targetOption && isLengthLimited) {
        let sanitizedText = `${targetOption.text.slice(
          0,
          MAX_ALLOWED_TEXT_LENGTH
        )}`;
        if (targetOption.text.length > MAX_ALLOWED_TEXT_LENGTH) {
          sanitizedText += '...';
        }
        targetOption.text = sanitizedText;
      }
      return targetOption;
    }, [value, isLengthLimited]);

    const onChangeHandler = useCallback(
      (newOption) => {
        onChange({
          target: {
            name,
            value:
              (variant === 'roundedGreenClosable' ||
                variant === 'roundedVioletClosable') &&
              newOption === null
                ? 'not_applied'
                : (newOption as SelectOption)?.value,
          },
        });
      },
      [onChange, name, variant]
    );

    return (
      <FormControl {...containerStyles}>
        <FieldTitle
          label={label}
          isRequired={isRequired}
          variant={labelVariant}
          starColor={starColor}
        />

        <InputGroup flexDirection='column' data-testid='chakra-select'>
          <BaseSelect
            aria-label={label}
            ref={
              ref as Ref<SelectInstance<unknown, boolean, GroupBase<unknown>>>
            }
            errorBorderColor='errors.200'
            placeholder={placeholder}
            options={options}
            isInvalid={!!errorMessage}
            chakraStyles={variantStyles}
            isSearchable={isSearchable}
            {...props}
            value={selectedOption}
            getOptionLabel={(option) => (option as SelectOption)?.text || ''}
            onChange={onChangeHandler}
          />

          {!occultErrorMessage && errorMessage && (
            <Error innerHTML={errorMessage} />
          )}
        </InputGroup>

        {errorMessage && showErrorIcon && <FormIconError />}
      </FormControl>
    );
  }
);

Select.displayName = 'Select';

export default Select;
